import { useEffect } from 'react';
import Portal from '@wconcept/wconcept-ui-renewal/components/atoms/Portal';
import useIsBody from '@wconcept/wconcept-ui-renewal/hooks/useIsBody';
import useResize from '@wconcept/wconcept-ui-renewal/hooks/useResize';
import { getScrollbarWidth } from '@wconcept/wconcept-common/utils/commonUtils';
import { isEmpty } from '@wconcept/wconcept-common/utils/baseUtils';
import {
  ModalContentType,
  ModalProps,
} from '@wconcept/wconcept-common/models/display/common';
import Modal from './index';

let activeElement: HTMLElement;

interface ModalContainerProps {
  modalContent: ModalContentType[];
  closeModal: (Component: React.JSXElementConstructor<any>) => void;
  openModal: <P = any, R = any, C = any>(
    Component: React.JSXElementConstructor<ModalProps<P, R, C>>,
    props: ModalProps<P, R, C>
  ) => void;
  isActiveModal: (
    Component: React.JSXElementConstructor<any>,
    active: boolean
  ) => void;
}

function ModalContainer({
  modalContent,
  closeModal,
  openModal,
  isActiveModal,
}: ModalContainerProps) {
  const { isBody } = useIsBody();
  const { isDesktop } = useResize();

  useEffect(() => {
    if (Array.isArray(modalContent) && !isEmpty(modalContent)) {
      const active = modalContent.filter(v => v.isActive);
      const activeNonModal = modalContent.filter(
        v => v.params.type === 'nonModal'
      );
      if (active.length === activeNonModal.length) {
        if (isDesktop) document.body.style.paddingRight = '';
        isBody?.classList.remove('no-scroll', 'no-lock');
      } else if (active.length > 0) {
        if (isDesktop) {
          document.body.style.paddingRight = getScrollbarWidth();
        } else {
          document.body.style.paddingRight = '';
        }
        isBody?.classList.add('no-scroll', 'no-lock');

        setTimeout(() => {
          activeElement = document.activeElement as HTMLElement;
          (document.querySelector('.modal-content') as HTMLElement)?.focus();
        }, 200);
      }
    } else if (isEmpty(modalContent)) {
      if (isDesktop) document.body.style.paddingRight = '';
      isBody?.classList.remove('no-scroll', 'no-lock');
    }
  }, [isBody, isDesktop, modalContent]);

  if (Array.isArray(modalContent) && !isEmpty(modalContent)) {
    return (
      <>
        {modalContent.map((modal, i) => {
          if (modal) {
            const { Component, params, isActive } = modal;
            const { onClose, onSubmit, onClick, ...restProps } = params;

            const handleClose = (type?: string) => {
              isActiveModal(Component, false);
              closeModal(Component);
              activeElement?.focus();
            };

            const handleSubmit = async (data?: unknown) => {
              if (typeof onSubmit === 'function') onSubmit(data);
              handleClose();
            };

            const handleClick = (e: any) => {
              if (onClick) {
                onClick(e);
              }
            };

            const handleOnClose = (type?: string) => {
              if (typeof onClose === 'function') {
                onClose(type);
              }
              handleClose(type);
            };

            if (isBody) {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <Portal el={isBody} key={i}>
                  <Modal
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    {...restProps}
                    isOpen={isActive}
                    onClose={handleOnClose}
                  >
                    <Component
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      onClose={handleOnClose}
                      onSubmit={handleSubmit}
                      onClick={handleClick}
                      value={restProps.value}
                    />
                  </Modal>
                </Portal>
              );
            }
            return null;
          }
          return null;
        })}
      </>
    );
  }
  return null;
}

export default ModalContainer;
